/**
 * HeatOutputChart.tsx
 * 
 * Simple SVG chart showing heat output vs time for Appendix D.
 * Two traces: steady modulating system vs highly reactive on-demand system.
 * Area under both curves is the same (same total heat delivered).
 */

interface HeatOutputChartProps {
  width?: number;
  height?: number;
}

export function HeatOutputChart({ width = 600, height = 300 }: HeatOutputChartProps) {
  // Chart dimensions
  const margin = { top: 40, right: 40, bottom: 50, left: 60 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  // Data points for steady modulating system
  // Smooth ramp up, long flat section, gentle ramp down
  const steadyData = [
    { time: 0, output: 0 },
    { time: 1, output: 40 },
    { time: 2, output: 60 },
    { time: 3, output: 65 },
    { time: 4, output: 65 },
    { time: 5, output: 65 },
    { time: 6, output: 65 },
    { time: 7, output: 65 },
    { time: 8, output: 60 },
    { time: 9, output: 50 },
    { time: 10, output: 30 },
    { time: 11, output: 10 },
    { time: 12, output: 0 },
  ];

  // Data points for highly reactive system
  // Ramp up, repeated high spikes, frequent transitions
  const reactiveData = [
    { time: 0, output: 0 },
    { time: 0.5, output: 90 },
    { time: 1, output: 95 },
    { time: 1.5, output: 20 },
    { time: 2, output: 10 },
    { time: 2.5, output: 85 },
    { time: 3, output: 95 },
    { time: 3.5, output: 25 },
    { time: 4, output: 15 },
    { time: 4.5, output: 80 },
    { time: 5, output: 90 },
    { time: 5.5, output: 30 },
    { time: 6, output: 20 },
    { time: 6.5, output: 85 },
    { time: 7, output: 90 },
    { time: 7.5, output: 35 },
    { time: 8, output: 25 },
    { time: 8.5, output: 75 },
    { time: 9, output: 80 },
    { time: 9.5, output: 40 },
    { time: 10, output: 30 },
    { time: 10.5, output: 15 },
    { time: 11, output: 5 },
    { time: 11.5, output: 0 },
    { time: 12, output: 0 },
  ];

  // Scale functions
  const xScale = (t: number) => (t / 12) * chartWidth;
  const yScale = (v: number) => chartHeight - (v / 100) * chartHeight;

  // Generate path for line
  const generatePath = (data: typeof steadyData) => {
    return data
      .map((point, i) => {
        const x = xScale(point.time);
        const y = yScale(point.output);
        return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
      })
      .join(' ');
  };

  const steadyPath = generatePath(steadyData);
  const reactivePath = generatePath(reactiveData);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="dp-heat-output-chart"
      role="img"
      aria-label="Heat output vs time comparison showing steady modulating system versus highly reactive on-demand system"
    >
      <title>
        Heat output comparison: A steady modulating system maintains consistent output over time (solid line), 
        while a highly reactive system shows frequent high spikes and cycling (dashed line). 
        Both deliver the same total heat (equal area under curves).
      </title>
      <defs>
        <style>{`
          .chart-axis { stroke: #64748b; stroke-width: 1.5; }
          .chart-grid { stroke: #e2e8f0; stroke-width: 1; stroke-dasharray: 4,4; }
          .chart-label { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 13px; fill: #475569; }
          .chart-title { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 14px; font-weight: 600; fill: #1e293b; }
          .steady-line { stroke: #2563eb; stroke-width: 2.5; fill: none; }
          .reactive-line { stroke: #dc2626; stroke-width: 2.5; fill: none; stroke-dasharray: 5,3; }
          .annotation { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 12px; fill: #64748b; font-style: italic; }
          .legend-text { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 12px; fill: #1e293b; }
        `}</style>
      </defs>

      {/* Chart group */}
      <g transform={`translate(${margin.left}, ${margin.top})`}>
        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map((v) => (
          <line
            key={`grid-${v}`}
            x1={0}
            y1={yScale(v)}
            x2={chartWidth}
            y2={yScale(v)}
            className="chart-grid"
          />
        ))}

        {/* Axes */}
        <line x1={0} y1={0} x2={0} y2={chartHeight} className="chart-axis" />
        <line x1={0} y1={chartHeight} x2={chartWidth} y2={chartHeight} className="chart-axis" />

        {/* Y-axis labels */}
        <text x={-10} y={yScale(100)} textAnchor="end" alignmentBaseline="middle" className="chart-label">
          High
        </text>
        <text x={-10} y={yScale(0)} textAnchor="end" alignmentBaseline="middle" className="chart-label">
          Low
        </text>

        {/* Y-axis title */}
        <text
          x={-45}
          y={chartHeight / 2}
          textAnchor="middle"
          transform={`rotate(-90, -45, ${chartHeight / 2})`}
          className="chart-title"
        >
          Heat Output
        </text>

        {/* X-axis labels */}
        <text x={0} y={chartHeight + 25} textAnchor="start" className="chart-label">
          0h
        </text>
        <text x={chartWidth / 2} y={chartHeight + 25} textAnchor="middle" className="chart-label">
          6h
        </text>
        <text x={chartWidth} y={chartHeight + 25} textAnchor="end" className="chart-label">
          12h
        </text>

        {/* X-axis title */}
        <text x={chartWidth / 2} y={chartHeight + 40} textAnchor="middle" className="chart-title">
          Time
        </text>

        {/* Steady line */}
        <path d={steadyPath} className="steady-line" />

        {/* Reactive line */}
        <path d={reactivePath} className="reactive-line" />

        {/* Annotation about equal area */}
        <text x={chartWidth / 2} y={-20} textAnchor="middle" className="annotation">
          Area under both curves is the same (same total heat delivered)
        </text>
      </g>

      {/* Legend */}
      <g transform={`translate(${margin.left}, ${height - 20})`}>
        {/* Steady system legend */}
        <line x1={0} y1={0} x2={30} y2={0} className="steady-line" />
        <text x={35} y={0} alignmentBaseline="middle" className="legend-text">
          Steady, modulating system
        </text>

        {/* Reactive system legend */}
        <line x1={chartWidth - 250} y1={0} x2={chartWidth - 220} y2={0} className="reactive-line" />
        <text x={chartWidth - 215} y={0} alignmentBaseline="middle" className="legend-text">
          Highly reactive / on-demand system
        </text>
      </g>
    </svg>
  );
}
